import { PLATFORM_CONTENT_SCOPE_KEY } from '../constants/platformContentScope';
import type { SchoolUserAdminStats, UserStatDetailKind, UserStatDetailPayload } from '../types/adminUserStats';
import type { SchoolLegalDocumentRow, SchoolLegalKind } from '../types/schoolLegalDocument';
import type { SchoolNoticeRow } from '../types/schoolNotice';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const LEGAL_CACHE_VERSION = 'v1';
const NOTICES_CACHE_VERSION = 'v1';
const USER_STATS_CACHE_VERSION = 'v1';
const USER_STAT_DETAIL_CACHE_VERSION = 'v2';

function canWrite(): boolean {
  return canWriteSchoolScopedDataCache(PLATFORM_CONTENT_SCOPE_KEY);
}

function legalListKey(kind: SchoolLegalKind): string {
  return `cp.platformAdmin.legal.${LEGAL_CACHE_VERSION}.${kind}`;
}

function noticesListKey(): string {
  return `cp.platformAdmin.notices.${NOTICES_CACHE_VERSION}`;
}

function userStatsKey(workspaceId: string): string {
  return `cp.platformAdmin.userStats.${USER_STATS_CACHE_VERSION}.${workspaceId.trim()}`;
}

function userStatDetailKey(workspaceId: string, kind: UserStatDetailKind): string {
  return `cp.platformAdmin.userStatDetail.${USER_STAT_DETAIL_CACHE_VERSION}.${workspaceId.trim()}.${kind}`;
}

function normalizeLegalRows(raw: unknown): SchoolLegalDocumentRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is SchoolLegalDocumentRow => !!row && typeof row === 'object' && 'id' in row);
}

function normalizeNoticeRows(raw: unknown): SchoolNoticeRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is SchoolNoticeRow => !!row && typeof row === 'object' && 'id' in row);
}

/** 목록의 head 시그니처: 건수 + `id:updated_at` 조합(정렬로 순서 무관). id/updated_at만 있으면 되므로 head 조회에도 재사용 가능. */
export function buildListHeadVersion(rows: Array<{ id: string; updated_at: string }>): string {
  const parts = rows
    .map((r) => `${r.id}:${r.updated_at}`)
    .sort();
  return `${parts.length}|${parts.join(',')}`;
}

export function loadSchoolLegalDocumentsCache(kind: SchoolLegalKind): {
  found: boolean;
  rows: SchoolLegalDocumentRow[];
  version: string | null;
} {
  if (typeof window === 'undefined') return { found: false, rows: [], version: null };
  try {
    const raw = deviceCacheStorage.getItem(legalListKey(kind));
    if (!raw) return { found: false, rows: [], version: null };
    const parsed = JSON.parse(raw) as { rows?: unknown; version?: string } | SchoolLegalDocumentRow[];
    if (Array.isArray(parsed)) return { found: true, rows: normalizeLegalRows(parsed), version: null };
    return {
      found: true,
      rows: normalizeLegalRows(parsed?.rows),
      version: typeof parsed?.version === 'string' ? parsed.version : null,
    };
  } catch {
    return { found: false, rows: [], version: null };
  }
}

export function saveSchoolLegalDocumentsCache(
  kind: SchoolLegalKind,
  rows: SchoolLegalDocumentRow[],
  options?: { force?: boolean; version?: string | null }
): void {
  if (typeof window === 'undefined') return;
  if (!options?.force && !canWrite()) return;
  try {
    deviceCacheStorage.setItem(
      legalListKey(kind),
      JSON.stringify({ rows, version: options?.version ?? null, savedAt: Date.now() })
    );
  } catch {
    /* quota */
  }
}

export function loadSchoolNoticesCache(): { found: boolean; rows: SchoolNoticeRow[]; version: string | null } {
  if (typeof window === 'undefined') return { found: false, rows: [], version: null };
  try {
    const raw = deviceCacheStorage.getItem(noticesListKey());
    if (!raw) return { found: false, rows: [], version: null };
    const parsed = JSON.parse(raw) as { rows?: unknown; version?: string } | SchoolNoticeRow[];
    if (Array.isArray(parsed)) return { found: true, rows: normalizeNoticeRows(parsed), version: null };
    return {
      found: true,
      rows: normalizeNoticeRows(parsed?.rows),
      version: typeof parsed?.version === 'string' ? parsed.version : null,
    };
  } catch {
    return { found: false, rows: [], version: null };
  }
}

export function saveSchoolNoticesCache(
  rows: SchoolNoticeRow[],
  options?: { force?: boolean; version?: string | null }
): void {
  if (typeof window === 'undefined') return;
  if (!options?.force && !canWrite()) return;
  try {
    deviceCacheStorage.setItem(
      noticesListKey(),
      JSON.stringify({ rows, version: options?.version ?? null, savedAt: Date.now() })
    );
  } catch {
    /* quota */
  }
}

export function loadAdminUserStatsCache(workspaceId: string): {
  found: boolean;
  stats: SchoolUserAdminStats;
  /** 최근 저장 시점의 head 시그니처(계정수·팀수·이용권 최신 updated_at 조합). 없으면 재검증 시 무조건 풀 로드. */
  version: string | null;
} {
  const sid = workspaceId.trim();
  const empty = { found: false, stats: { registeredAccountCount: null, teamCount: 0, passCategoryCounts: null }, version: null };
  if (!sid || typeof window === 'undefined') {
    return empty;
  }
  try {
    const raw = deviceCacheStorage.getItem(userStatsKey(sid));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as { stats?: SchoolUserAdminStats; version?: string };
    const stats = parsed?.stats;
    if (!stats || typeof stats !== 'object') {
      return empty;
    }
    const rawCats = stats.passCategoryCounts;
    if (!rawCats || typeof rawCats !== 'object') {
      return empty;
    }
    const legacyEvent = Number((rawCats as { event?: number }).event) || 0;
    const eventFreeRaw = Number((rawCats as { eventFree?: number }).eventFree) || 0;
    const eventPaidRaw = Number((rawCats as { eventPaid?: number }).eventPaid) || 0;
    const passCategoryCounts = {
      beta: Number(rawCats.beta) || 0,
      free: Number(rawCats.free) || 0,
      eventFree: eventFreeRaw + (eventFreeRaw === 0 && eventPaidRaw === 0 ? legacyEvent : 0),
      eventPaid: eventPaidRaw,
      individual: Number(rawCats.individual) || 0,
      group: Number(rawCats.group) || 0,
    };
    return {
      found: true,
      stats: {
        registeredAccountCount:
          stats.registeredAccountCount === null || stats.registeredAccountCount === undefined
            ? null
            : Number(stats.registeredAccountCount),
        teamCount: Number(stats.teamCount) || 0,
        passCategoryCounts,
      },
      version: typeof parsed?.version === 'string' ? parsed.version : null,
    };
  } catch {
    return empty;
  }
}

export function saveAdminUserStatsCache(
  workspaceId: string,
  stats: SchoolUserAdminStats,
  options?: { force?: boolean; version?: string | null }
): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  if (!options?.force && !canWrite()) return;
  try {
    deviceCacheStorage.setItem(
      userStatsKey(sid),
      JSON.stringify({ stats, version: options?.version ?? null, savedAt: Date.now() })
    );
  } catch {
    /* quota */
  }
}

export function loadAdminUserStatDetailCache(
  workspaceId: string,
  kind: UserStatDetailKind
): { found: boolean; payload: UserStatDetailPayload | null } {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return { found: false, payload: null };
  try {
    const raw = deviceCacheStorage.getItem(userStatDetailKey(sid, kind));
    if (!raw) return { found: false, payload: null };
    const parsed = JSON.parse(raw) as { payload?: UserStatDetailPayload };
    const payload = parsed?.payload;
    if (!payload || typeof payload !== 'object' || !('kind' in payload)) {
      return { found: false, payload: null };
    }
    if (payload.kind === 'allAccounts') {
      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      if (
        rows.length > 0 &&
        rows.some(
          (r) =>
            !r ||
            typeof r !== 'object' ||
            !('pass_category' in r) ||
            !('last_sign_in_at' in r) ||
            !('last_active_on' in r)
        )
      ) {
        return { found: false, payload: null };
      }
    }
    return { found: true, payload };
  } catch {
    return { found: false, payload: null };
  }
}

export function saveAdminUserStatDetailCache(
  workspaceId: string,
  kind: UserStatDetailKind,
  payload: UserStatDetailPayload,
  options?: { force?: boolean }
): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  if (!options?.force && !canWrite()) return;
  try {
    deviceCacheStorage.setItem(userStatDetailKey(sid, kind), JSON.stringify({ payload, savedAt: Date.now() }));
  } catch {
    /* quota */
  }
}

/** 집계 새로고침: 통계·상세 목록 캐시 제거 */
export function clearAdminUserStatsCaches(workspaceId: string): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(userStatsKey(sid));
    const kinds: UserStatDetailKind[] = ['allAccounts', 'teamsWithMembers'];
    for (const kind of kinds) {
      deviceCacheStorage.removeItem(userStatDetailKey(sid, kind));
    }
  } catch {
    /* ignore */
  }
}
